/**
 * One Maillot — API layer using Supabase directly (no backend)
 * All CRUD goes through supabase-js → Supabase PostgreSQL
 */
import { supabase } from './supabase';

// ============================================================
// Backend secured admin helpers (used for writes that need RLS bypass)
// ============================================================
// Same-origin by default. Both Vercel and the Emergent preview route /api/*
// to their own backend automatically (Vercel via API routes + vercel.json rewrites,
// Emergent preview via K8s ingress on port 8001). This means the deployed site
// no longer depends on REACT_APP_BACKEND_URL pointing to a specific server.
const BACKEND = '';

async function _authHeaders() {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) throw new Error('Non authentifié');
  return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
}

// ============================================================
// PRODUCTS
// ============================================================
// Light field set used for product cards (homepage carousels, catalog grid).
// Reduces payload by ~50% vs select=*.
const CARD_FIELDS = 'product_id,slug,name,team,subcategory,price,original_price,images,badges,version,gender';

/**
 * Retry a Supabase query up to 2 times on transient errors (network, 5xx).
 * Adds a 6 s timeout to fail fast instead of hanging on a stuck request.
 */
async function _retry(queryFn, attempts = 2) {
  let lastErr;
  for (let i = 0; i <= attempts; i++) {
    try {
      const timeout = new Promise((_, rej) => setTimeout(() => rej(new Error('timeout')), 6000));
      const result = await Promise.race([queryFn(), timeout]);
      if (result?.error) throw result.error;
      return result;
    } catch (e) {
      lastErr = e;
      if (i < attempts) {
        // Exponential backoff: 200 ms → 600 ms
        await new Promise(r => setTimeout(r, 200 * Math.pow(3, i)));
      }
    }
  }
  throw lastErr;
}

export async function getProducts({
  category, subcategory, league, team, country, season,
  gender, version, search, min_price, max_price, badge,
  sort = 'newest', page = 1, limit = 24,
  light = false, withCount = true,
} = {}) {
  const fields = light ? CARD_FIELDS : '*';
  const countOpt = withCount ? { count: 'exact' } : undefined;

  // Group of subcategories that all hold "national-team" jerseys.
  // When a user filters by ANY of these, we want all of them included so that
  // a France CDM-2026 jersey shows up under "Sélections Nationales" too — and
  // vice versa. Accent-insensitive and case-insensitive comparison.
  const NATIONAL_TEAM_BUCKETS = ['Sélections Nationales', 'Coupe du Monde 2026'];
  const _stripAccents = (s) => (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const isNationalBucket = subcategory && NATIONAL_TEAM_BUCKETS.some(
    (b) => _stripAccents(subcategory).includes(_stripAccents(b))
  );

  const buildQuery = () => {
    let query = supabase.from('products').select(fields, countOpt).eq('visible', true);
    if (category) query = query.ilike('category', `%${category}%`);
    if (subcategory) {
      if (isNationalBucket) {
        // OR across both buckets — catches CDM 2026 + Sélections Nationales products.
        const orClauses = NATIONAL_TEAM_BUCKETS.map((b) => `subcategory.ilike.%${b}%`).join(',');
        query = query.or(orClauses);
      } else {
        query = query.ilike('subcategory', `%${subcategory}%`);
      }
    }
    if (league) query = query.ilike('league', `%${league}%`);
    if (team) query = query.ilike('team', `%${team}%`);
    if (country) query = query.ilike('country', `%${country}%`);
    if (season) query = query.eq('season', season);
    if (gender) query = query.eq('gender', gender);
    if (version) query = query.eq('version', version);
    if (search) query = query.or(`name.ilike.%${search}%,team.ilike.%${search}%,league.ilike.%${search}%,country.ilike.%${search}%`);
    if (min_price != null) query = query.gte('price', min_price);
    if (max_price != null) query = query.lte('price', max_price);
    if (badge) query = query.contains('badges', [badge]);

    const sortMap = { newest: ['created_at', { ascending: false }], price_asc: ['price', { ascending: true }], price_desc: ['price', { ascending: false }], name: ['name', { ascending: true }] };
    const [col, opts] = sortMap[sort] || sortMap.newest;
    query = query.order(col, opts).order('product_id', { ascending: true });

    const from = (page - 1) * limit;
    return query.range(from, from + limit - 1);
  };

  const { data, count } = await _retry(() => buildQuery());
  return { data: { products: data || [], total: count || 0, page, pages: Math.ceil((count || 0) / limit) || 1 } };
}

/**
 * Lightweight: returns aggregated team counts for the World Cup mega-menu.
 * Replaces the previous expensive `getProducts({ subcategory: 'Sélections Nationales', limit: 500 })`
 * which fetched ~186KB of full product rows just to count teams.
 */
export async function getWcTeams() {
  const { data } = await supabase
    .from('products')
    .select('team')
    .eq('visible', true)
    .or('subcategory.ilike.%Sélections Nationales%,subcategory.eq.Coupe du Monde 2026')
    .limit(1000);
  const counts = {};
  (data || []).forEach((p) => { if (p.team) counts[p.team] = (counts[p.team] || 0) + 1; });
  const teams = Object.entries(counts)
    .sort((a, b) => a[0].localeCompare(b[0], 'fr', { sensitivity: 'base' }))
    .map(([team, count]) => ({ team, count }));
  return { data: { teams } };
}

export async function getProduct(slugOrId) {
  // Try slug first; fall back to product_id so order-history links keep working
  // even when only the product_id was stored in the order item.
  let { data: product, error } = await supabase.from('products').select('*').eq('slug', slugOrId).maybeSingle();
  if (!product && (slugOrId || '').startsWith('prod_')) {
    const fallback = await supabase.from('products').select('*').eq('product_id', slugOrId).maybeSingle();
    product = fallback.data;
    error = fallback.error;
  }
  if (error || !product) throw error || new Error('Product not found');
  const { data: related } = await supabase.from('products').select('*').eq('category', product.category).eq('visible', true).neq('product_id', product.product_id).limit(4);
  return { data: { product, related: related || [] } };
}

export async function searchProducts(q) {
  if (!q || q.length < 2) return { data: { products: [] } };
  const { data } = await supabase.from('products').select('*').eq('visible', true).or(`name.ilike.%${q}%,team.ilike.%${q}%,league.ilike.%${q}%,country.ilike.%${q}%`).limit(10);
  return { data: { products: data || [] } };
}

// ============================================================
// CATEGORIES
// ============================================================
export async function getCategories() {
  const { data } = await supabase.from('categories').select('*');
  return { data: { categories: data || [] } };
}

export async function getTeamsBySubcategory() {
  // Fetch all products in pages of 1000 (Supabase default max)
  let allProducts = [];
  let from = 0;
  const pageSize = 1000;
  while (true) {
    const { data } = await supabase.from('products').select('subcategory, team').eq('visible', true).range(from, from + pageSize - 1);
    if (!data || data.length === 0) break;
    allProducts = allProducts.concat(data);
    if (data.length < pageSize) break;
    from += pageSize;
  }
  const grouped = {};
  allProducts.forEach(p => {
    if (!p.subcategory || !p.team) return;
    if (!grouped[p.subcategory]) grouped[p.subcategory] = {};
    grouped[p.subcategory][p.team] = (grouped[p.subcategory][p.team] || 0) + 1;
  });
  const result = {};
  Object.entries(grouped).forEach(([sub, teams]) => {
    result[sub] = Object.entries(teams).map(([team, count]) => ({ team, count })).sort((a, b) => b.count - a.count);
  });
  return { data: { teams_by_subcategory: result } };
}

// ============================================================
// ORDERS — read-only from frontend (writes go through backend /api/create-checkout-session)
// ============================================================
export async function getOrders() {
  const { data } = await supabase.from('orders').select('*').order('created_at', { ascending: false }).limit(50);
  return { data: { orders: data || [] } };
}

// ============================================================
// WISHLIST
// ============================================================
export async function toggleWishlist(product_id) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Non authentifié');
  const { data: existing } = await supabase.from('wishlist').select('id').eq('user_id', user.id).eq('product_id', product_id).single();
  if (existing) {
    await supabase.from('wishlist').delete().eq('user_id', user.id).eq('product_id', product_id);
    return { data: { action: 'removed', product_id } };
  }
  await supabase.from('wishlist').insert({ user_id: user.id, product_id });
  return { data: { action: 'added', product_id } };
}

export async function getWishlist() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { data: { products: [] } };
  const { data: items } = await supabase.from('wishlist').select('product_id').eq('user_id', user.id);
  if (!items?.length) return { data: { products: [] } };
  const ids = items.map(i => i.product_id);
  const { data: products } = await supabase.from('products').select('*').in('product_id', ids);
  return { data: { products: products || [] } };
}

// ============================================================
// NEWSLETTER
// ============================================================
export async function subscribeNewsletter(email) {
  const { error } = await supabase.from('newsletter').insert({ email });
  if (error?.code === '23505') return { data: { message: 'Vous êtes déjà inscrit' } };
  if (error) throw error;
  return { data: { message: 'Inscription réussie' } };
}

// ============================================================
// AUTH (Supabase Auth)
// ============================================================
export async function registerUser({ email, password, name }) {
  const { data, error } = await supabase.auth.signUp({
    email, password,
    options: { data: { name } },
  });
  if (error) throw { response: { data: { detail: error.message } } };
  return { data: { user_id: data.user?.id, email, name } };
}

export async function loginUser({ email, password }) {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw { response: { data: { detail: error.message } } };
  // Fetch profile for is_admin
  const { data: profile } = await supabase.from('profiles').select('is_admin, name').eq('id', data.user.id).single();
  return { data: { user_id: data.user.id, email: data.user.email, name: profile?.name || '', is_admin: profile?.is_admin || false } };
}

export async function getMe() {
  const { data: { user }, error } = await supabase.auth.getUser();
  if (error || !user) throw { response: { status: 401 } };
  const { data: profile } = await supabase.from('profiles').select('name, picture, is_admin').eq('id', user.id).single();
  return { data: { user_id: user.id, email: user.email, name: profile?.name || '', picture: profile?.picture, is_admin: profile?.is_admin || false } };
}

export async function logoutUser() {
  await supabase.auth.signOut();
  return { data: { message: 'Déconnecté' } };
}

// Google session — not used in direct Supabase mode
export async function googleSession() { return { data: {} }; }

// ============================================================
// ADMIN
// ============================================================
export async function adminDashboard() {
  const { count: products } = await supabase.from('products').select('*', { count: 'exact', head: true });
  const { count: orders } = await supabase.from('orders').select('*', { count: 'exact', head: true });
  const { count: users } = await supabase.from('profiles').select('*', { count: 'exact', head: true });
  const { count: newsletter } = await supabase.from('newsletter').select('*', { count: 'exact', head: true });

  const { data: orderData } = await supabase.from('orders').select('total, status, created_at, order_id, items, shipping, user_id').order('created_at', { ascending: false });
  const revenue = (orderData || []).reduce((sum, o) => sum + (parseFloat(o.total) || 0), 0);
  const recent_orders = (orderData || []).slice(0, 5);
  const orders_by_status = {};
  (orderData || []).forEach(o => { orders_by_status[o.status] = (orders_by_status[o.status] || 0) + 1; });

  const { data: leagueData } = await supabase.from('products').select('subcategory');
  const products_by_league = {};
  (leagueData || []).forEach(p => { if (p.subcategory) products_by_league[p.subcategory] = (products_by_league[p.subcategory] || 0) + 1; });

  return { data: { products: products || 0, orders: orders || 0, users: users || 0, newsletter: newsletter || 0, revenue: Math.round(revenue * 100) / 100, recent_orders, products_by_league, orders_by_status } };
}

export async function adminProducts({ page = 1, limit = 20, search = '', subcategory = '', team = '', season = '', gender = '', version = '', productType = '' } = {}) {
  let query = supabase.from('products').select('*', { count: 'exact' });
  if (search) query = query.or(`name.ilike.%${search}%,team.ilike.%${search}%`);
  if (subcategory) query = query.eq('subcategory', subcategory);
  if (team) query = query.eq('team', team);
  if (season) query = query.eq('season', season);
  if (gender) query = query.eq('gender', gender);
  if (version) query = query.eq('version', version);
  // Product-type filter (based on name patterns)
  const pt = (productType || '').toLowerCase();
  if (pt === 'long_sleeve') {
    query = query.ilike('name', '%manches longues%');
  } else if (pt === 'short') {
    query = query.ilike('name', '%short%');
  } else if (pt === 'retro') {
    query = query.or('name.ilike.%rétro%,name.ilike.%retro%');
  } else if (pt === 'jersey') {
    // Exclut manches longues / shorts / rétro
    query = query
      .not('name', 'ilike', '%manches longues%')
      .not('name', 'ilike', '%short%')
      .not('name', 'ilike', '%rétro%')
      .not('name', 'ilike', '%retro%');
  }
  const from = (page - 1) * limit;
  query = query.order('product_id', { ascending: true }).range(from, from + limit - 1);
  const { data, count, error } = await query;
  if (error) throw error;
  return { data: { products: data || [], total: count || 0, page, pages: Math.ceil((count || 0) / limit) || 1 } };
}

export async function adminCreateProduct(productData) {
  const res = await fetch(`${BACKEND}/api/admin/products`, {
    method: 'POST',
    headers: await _authHeaders(),
    body: JSON.stringify(productData),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  const data = await res.json();
  return { data: { product_id: data.product_id, message: 'Produit créé' } };
}

export async function adminUpdateProduct(id, updates) {
  const clean = Object.fromEntries(Object.entries(updates).filter(([, v]) => v !== undefined && v !== null));
  const res = await fetch(`${BACKEND}/api/admin/products?id=${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: await _authHeaders(),
    body: JSON.stringify(clean),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  return { data: { message: 'Produit mis à jour' } };
}

export async function adminDeleteProduct(id) {
  const res = await fetch(`${BACKEND}/api/admin/products?id=${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: await _authHeaders(),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  return { data: { message: 'Produit supprimé' } };
}

export async function adminToggleVisibility(id, visible) {
  return adminUpdateProduct(id, { visible });
}

// ── Customer: my orders ──
export async function myOrders() {
  const res = await fetch(`${BACKEND}/api/orders/mine`, { headers: await _authHeaders() });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  return { data: await res.json() };
}

// ── Admin: numeroo10 import ──
export async function adminImportNumeroo10() {
  const res = await fetch(`${BACKEND}/api/admin/import/numeroo10`, {
    method: 'POST',
    headers: await _authHeaders(),
  });
  const raw = await res.text();
  let data = {};
  try { data = raw ? JSON.parse(raw) : {}; } catch { data = { detail: raw }; }
  if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
  return { data };
}

export async function adminBulkUpdatePrice(filters, { newPrice, originalPrice, dryRun = false } = {}) {
  // filters: { gender, version, productType, subcategory, team, season }
  const body = {
    new_price: Number(newPrice),
    gender: filters.gender || undefined,
    version: filters.version || undefined,
    product_type: filters.productType || undefined,
    subcategory: filters.subcategory || undefined,
    team: filters.team || undefined,
    season: filters.season || undefined,
    dry_run: dryRun,
  };
  if (originalPrice !== undefined) {
    body.original_price = originalPrice === null || originalPrice === ''
      ? null
      : Number(originalPrice);
  }
  const res = await fetch(`${BACKEND}/api/admin/products?action=bulk-price`, {
    method: 'POST',
    headers: await _authHeaders(),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  return { data: await res.json() };
}


export async function adminUploadImage(file) {
  const ext = file.name.split('.').pop() || 'png';
  const path = `admin/${crypto.randomUUID().slice(0, 10)}.${ext}`;
  const { error } = await supabase.storage.from('products').upload(path, file, { contentType: file.type });
  if (error) throw error;
  const { data: { publicUrl } } = supabase.storage.from('products').getPublicUrl(path);
  return { data: { url: publicUrl, path } };
}

// ============================================================
// ADMIN — Orders go through SECURED backend (RLS-friendly, server-side auth)
// ============================================================

export async function adminOrders({ page = 1, limit = 20, status = '', dateFrom = '', dateTo = '' } = {}) {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (status && status !== 'all') params.append('status', status);
  if (dateFrom) params.append('date_from', dateFrom);
  if (dateTo) params.append('date_to', dateTo);
  const res = await fetch(`${BACKEND}/api/admin/orders?${params}`, { headers: await _authHeaders() });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  const data = await res.json();
  return { data: { orders: data.orders, total: data.total, page: data.page, pages: data.pages } };
}

export async function adminUpdateOrder(id, { status, tracking_number, carrier } = {}) {
  const body = {};
  if (status !== undefined) body.status = status;
  if (tracking_number !== undefined) body.tracking_number = tracking_number;
  if (carrier !== undefined) body.carrier = carrier;
  const res = await fetch(`${BACKEND}/api/admin/orders?id=${encodeURIComponent(id)}&action=status`, {
    method: 'PATCH',
    headers: await _authHeaders(),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  return { data: await res.json() };
}

export async function adminUsers({ page = 1, limit = 50 } = {}) {
  const { data, count } = await supabase.from('profiles').select('*', { count: 'exact' }).order('created_at', { ascending: false }).range(0, limit - 1);
  return { data: { users: (data || []).map(u => ({ ...u, user_id: u.id })), total: count || 0, page } };
}

export async function adminPromoteUser(id, { is_admin }) {
  const { error } = await supabase.from('profiles').update({ is_admin }).eq('id', id);
  if (error) throw error;
  return { data: { message: 'Rôle mis à jour' } };
}

export async function adminSubcategories() {
  const { data } = await supabase.from('products').select('subcategory');
  const counts = {};
  (data || []).forEach(p => { if (p.subcategory) counts[p.subcategory] = (counts[p.subcategory] || 0) + 1; });
  const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]).map(([name, count]) => ({ name, count }));
  return { data: { subcategories: sorted } };
}

export async function adminTeams({ subcategory = '' } = {}) {
  let query = supabase.from('products').select('team');
  if (subcategory) query = query.eq('subcategory', subcategory);
  const { data } = await query;
  const counts = {};
  (data || []).forEach(p => { if (p.team) counts[p.team] = (counts[p.team] || 0) + 1; });
  const sorted = Object.entries(counts).sort((a, b) => a[0].localeCompare(b[0])).map(([name, count]) => ({ name, count }));
  return { data: { teams: sorted } };
}

export async function adminSeasons() {
  const { data } = await supabase.from('products').select('season');
  const counts = {};
  (data || []).forEach(p => { if (p.season) counts[p.season] = (counts[p.season] || 0) + 1; });
  const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]).map(([name, count]) => ({ name, count }));
  return { data: { seasons: sorted } };
}

// ============================================================
// ADMIN ACCOUNT MANAGEMENT
// ============================================================
export async function adminCreateUser({ email, password, is_admin = true }) {
  const res = await fetch('/api/admin/users', {
    method: 'POST',
    headers: await _authHeaders(),
    body: JSON.stringify({ email, password, is_admin }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

// ============================================================
// PROMO CODES
// ============================================================

// Public — validates a promo code against the current cart, returns discount preview.
export async function validatePromoCode(code, items) {
  const res = await fetch('/api/promo/validate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code, items }),
  });
  const data = await res.json().catch(() => ({}));
  return data; // { valid, error?, discount?, finalAmount?, ... }
}

// Admin — list/create/edit/delete promo codes
export async function adminListPromoCodes() {
  const res = await fetch('/api/admin/promo-codes', { headers: await _authHeaders() });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `HTTP ${res.status}`);
  return res.json();
}
export async function adminCreatePromoCode(payload) {
  const res = await fetch('/api/admin/promo-codes', {
    method: 'POST',
    headers: await _authHeaders(),
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}
export async function adminUpdatePromoCode(id, patch) {
  const res = await fetch(`/api/admin/promo-codes?id=${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: await _authHeaders(),
    body: JSON.stringify(patch),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}
export async function adminDeletePromoCode(id) {
  const res = await fetch(`/api/admin/promo-codes?id=${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: await _authHeaders(),
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `HTTP ${res.status}`);
  return res.json();
}

// ============================================================
// SOCIAL EMBEDS (Instagram / TikTok / Snapchat — Admin "Vitrine sociale")
// ============================================================

// Public — used by HomePage
export async function listSocialEmbeds() {
  const res = await fetch('/api/social-embeds');
  if (!res.ok) return { embeds: [] };
  return res.json();
}

// Admin
export async function adminListSocialEmbeds() {
  const res = await fetch('/api/social-embeds?admin=1', { headers: await _authHeaders() });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `HTTP ${res.status}`);
  return res.json();
}
export async function adminCreateSocialEmbed(payload) {
  const res = await fetch('/api/social-embeds', {
    method: 'POST',
    headers: await _authHeaders(),
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}
export async function adminUpdateSocialEmbed(id, patch) {
  const res = await fetch(`/api/social-embeds?id=${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: await _authHeaders(),
    body: JSON.stringify(patch),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}
export async function adminDeleteSocialEmbed(id) {
  const res = await fetch(`/api/social-embeds?id=${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: await _authHeaders(),
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `HTTP ${res.status}`);
  return res.json();
}


export async function seedAdmin() {
  // No-op in Supabase mode — admin is set via profiles table
  return { data: { message: 'Use Supabase Dashboard to manage admins' } };
}

export async function seedDatabase() {
  return { data: { message: 'Data already in Supabase' } };
}
